import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import _get from 'lodash/get';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTicket } from '@fortawesome/free-solid-svg-icons';

import FluidImg from 'Components/Utility/FluidImg';
import Price from 'Components/Utility/Price/Price';
import PickAndMixHits from 'Components/PickAndMix/PickAndMixHits';
import PickAndMixTierBoxes from 'Components/PickAndMix/PickAndMixTierBoxes';
import PnmSelectedGameCollapsible from 'Components/PickAndMix/PnmSelectedGameCollapsible';
import PickAndMixSubtotal from 'Components/PickAndMix/PickAndMixSubtotal';
import ProductPageTimer from 'Components/Product/Timer/ProductPageTimer';
import PickAndMixAddAll from 'Components/PickAndMix/PickAndMixAddAll';
import PillFilters from 'Components/Utility/PillFilters';
import PickAndMixPreselectedTab from 'Components/PickAndMix/PickAndMixPreselectedTab';
import DlcBadge from 'Components/Cards/DlcBadge';
import { getBundleTypeMessage } from 'Components/PickAndMix/PickAndMixHelper';

const PickAndMixMobileLayout = ({
  pickAndMix,
  selectedProducts,
  switchCarouselGame,
  hits,
  currentTab,
  handleTabChange,
  tabOptions,
  preselections,
  showRecommendedTab,
}) => {
  const {
    name,
    cover_image: cover,
    tiers,
    products,
    type,
    valid_until: endDate,
    valid_from: startDate,
    slug,
  } = pickAndMix;
  const { formatMessage } = useIntl();

  const bundleTypeMessage = formatMessage(getBundleTypeMessage(type, products));
  const firstTier = _get(tiers, '[0]');
  const secondTier = tiers?.[1];

  const showDlcBanner = name === 'Build your own Bundle: Command: Modern Operations DLC Campaigns';

  const SaveMoreMessage = () =>
    <div className="PickAndMixProductPage__content__saveMoreMessage">
      {firstTier &&
        <FormattedMessage
          id="PickAndMixMobileLayout.savemoremessage"
          defaultMessage="Build your own Bundle from {savingText}. Add {bundleType} to start saving. The more you add, the more you save!"
          values={{
            bundleType: bundleTypeMessage.toLocaleLowerCase(),
            savingText: <span className="PickAndMixProductPage__content__saveMoreMessage--primary">
              <FormattedMessage
                id="PickAndMixMobileLayout.savingText"
                defaultMessage="{firstTierQuantity} for {firstTierPrice}"
                values={{
                  firstTierQuantity: firstTier.quantity,
                  firstTierPrice: <Price price={firstTier.price} />,
                }}
              />
            </span>,
          }}
        />
      }
    </div>;

  return (
    <div className="PickAndMixProductPage__content container">
      <h1 className="PickAndMixProductPage__content__title">{name}</h1>
      <div className="d-none d-md-block"><SaveMoreMessage /></div>

      <div className="PickAndMixProductPage__content__coverTiersContainer">
        <div className="PickAndMixProductPage__content__cover responsive-image responsive-image--16by9">
          <FluidImg
            className="img-force-full"
            src={cover}
            alt={name}
            lazy={false}
            sizesOverride="(min-width: 576px) 524px, calc(100vw - 1rem)"
          />
        </div>

        <div className="d-block d-md-none"><SaveMoreMessage /></div>

        <div className="PickAndMixProductPage__content__tiersAddAll">
          <PickAndMixTierBoxes
            tiers={tiers}
            bundleTypeMessage={bundleTypeMessage}
            selectedProducts={selectedProducts}
            products={products}
          />

          <PickAndMixAddAll
            tiers={tiers}
            selectedProducts={selectedProducts}
            products={products}
          />
        </div>
      </div>

      {showDlcBanner &&
        <div className="PickAndMixProductPage__dlcBanner">
          <div className="PickAndMixProductPage__dlcBanner__icon">
            <DlcBadge size="large" />
          </div>
          <div className="PickAndMixProductPage__dlcBanner__message">
            <span className="PickAndMixProductPage__dlcBanner__message__title">
              <FormattedMessage
                id="PickAndMixMobileLayout.dlc.title"
                defaultMessage="Downloadable Content."
              />
            </span>&nbsp;
            <FormattedMessage
              id="PickAndMixMobileLayout.dlc.description.parent"
              defaultMessage="This content requires {parent} on the same platform in order to play"
              values={{
                parent: <Link to="/game/command-modern-operations">Command: Modern Operations</Link>,
              }}
            />
          </div>
        </div>
      }

      {slug === 'build-your-own-hollywood-music-and-sfx-bundle' && secondTier &&
        <div className="PickAndMixProductPage__couponBanner">
          <div className="PickAndMixProductPage__couponBanner__icon">
            <FontAwesomeIcon icon={faTicket} />
          </div>
          <div className="PickAndMixProductPage__couponBanner__message">
            <span className="PickAndMixProductPage__couponBanner__message__title">
              <FormattedMessage
                id="PickAndMixMobileLayout.freecoupon.title"
                defaultMessage="Free Coupon:"
              />
            </span>&nbsp;
            <FormattedMessage
              id="PickAndMixMobileLayout.coupon.description"
              defaultMessage="Spend {price} or more and instantly get a free coupon to redeem up to 10 tracks, which will be automatically added to your order"
              values={{
                price: <Price price={secondTier?.price} />,
              }}
            />
          </div>
        </div>
      }

      <div className="PickAndMixProductPage__tab__filters">
        {(showRecommendedTab || currentTab === 'recommended') &&
          <PillFilters
            mobilePills
            options={tabOptions}
            value={currentTab}
            onChange={handleTabChange}
          />
        }
      </div>


      {((showRecommendedTab && currentTab === 'recommended')) ?
        <PickAndMixPreselectedTab
          preselections={preselections}
          bundleMessageType={bundleTypeMessage}
        /> :

        <PickAndMixHits
          hits={hits}
          type={type}
          switchCarouselGame={switchCarouselGame}
          slug={slug}
        />
      }

      <div className="PickAndMixProductPage__stickySummary">
        <PnmSelectedGameCollapsible
          selectedProducts={selectedProducts}
        />

        <PickAndMixSubtotal
          tiers={tiers}
          selectedProducts={selectedProducts}
        />

        <ProductPageTimer
          bundlePage
          startDate={startDate ? Date.parse(startDate) : 0}
          endDate={endDate ? Date.parse(endDate) : 0}
          dayRestriciton={19}
        />
      </div>
    </div>
  );
};

PickAndMixMobileLayout.propTypes = {
  pickAndMix: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    cover_image: PropTypes.string,
    type: PropTypes.string,
    tiers: PropTypes.arrayOf(PropTypes.object),
    valid_until: PropTypes.string,
    valid_from: PropTypes.string,
  }),
  selectedProducts: PropTypes.arrayOf(PropTypes.object),
  switchCarouselGame: PropTypes.func.isRequired,
  hits: PropTypes.arrayOf(PropTypes.object),
  currentTab: PropTypes.string,
  handleTabChange: PropTypes.func.isRequired,
  tabOptions: PropTypes.arrayOf(PropTypes.object),
  preselections: PropTypes.arrayOf(PropTypes.object),
  showRecommendedTab: PropTypes.bool,
};

PickAndMixMobileLayout.defaultProps = {
  pickAndMix: null,
  selectedProducts: [],
  hits: [],
  tabOptions: [],
  preselections: [],
  showRecommendedTab: false,
  currentTab: 'all',
};

export default PickAndMixMobileLayout;
